import { DataObject } from '@shared/models/data-object.model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Step, StepComponent, WizardDef } from './model/wizard.model';

interface StepSet {
  [key: string]: Step<StepComponent>;
}

const isStepFulfilled = (step: Step<StepComponent>, data: { [key: string]: any }) => {
  const requiredFields = Object.entries(step.targetData)
    .filter(([, required]) => required)
    .map(([fieldName]) => fieldName);

  return requiredFields.every(field => data[field] != null);
};

export interface WizardState {
  currentStep: Step<StepComponent>;
  data: DataObject;
}

export class WizardDriverService {
  private stepsSet: StepSet;
  private initialStep: string | undefined;

  private _data = new BehaviorSubject(null);
  public data$: Observable<DataObject> = this._data.asObservable();

  private _showStopButton = new BehaviorSubject(null);
  public showStopButton$: Observable<DataObject> = this._showStopButton.asObservable();

  private _finished = new Subject<boolean>();
  public finished$: Observable<boolean> = this._finished.asObservable().pipe(filter(finished => finished));

  private _currentStepTopic = new BehaviorSubject<Step<any>>(null);
  currentStep$: Observable<Step<StepComponent>> = this._currentStepTopic.asObservable();

  constructor(wizardConfig: WizardDef) {
    this.stepsSet = wizardConfig.steps;
    this.initialStep = wizardConfig.initialStep;
    this._data.next(wizardConfig.data);
  }

  destroy() {
    this._data.complete();
    this._finished.complete();
    this._currentStepTopic.complete();
  }

  /**
   * returns the stepSet as array of Steps
   */
  get steps(): Array<Step<StepComponent>> {
    return this.stepsKeys.map(({ step }) => step);
  }

  /**
   * returns the stepSet as array of {key: string, step: Step}
   */
  get stepsKeys() {
    return Object.entries(this.stepsSet)
      .sort(([, stepA], [, stepB]) => stepA.stepNumber - stepB.stepNumber)
      .map(([key, step]) => ({ key, step }));
  }

  /**
   * Attempts to find the first valid step to be loaded when data is already
   * preselected before starting; else it will return the first step.
   * When initialStep is set it takes priority over the default logic.
   */
  start() {
    const [firstStep] = this.steps;
    if (this.initialStep && this.stepsSet[this.initialStep]) {
      this._currentStepTopic.next(this.stepsSet[this.initialStep]);
      return;
    }
    const currentStep = this.steps.find(step => !isStepFulfilled(step, this._data.getValue()));
    this._currentStepTopic.next(currentStep || firstStep);
  }

  /**
   * updates next currentStep stream if next exist, else it
   * streams finished
   */
  next() {
    const currentStep = this._currentStepTopic.getValue();
    if (currentStep.next != null) {
      const nextStep = this.stepsSet[currentStep.next];
      return this._currentStepTopic.next(nextStep);
    }
    return this._finished.next(true);
  }

  /**
   * Loads prev step if defined (ideally only first step will not have)
   */
  prev() {
    const currentStep = this._currentStepTopic.getValue();
    if (currentStep.prev != null) {
      const prevStep = this.stepsSet[currentStep.prev];
      return this._currentStepTopic.next(prevStep);
    }
  }

  loadStep(nextStep: Step<StepComponent>) {
    return this._currentStepTopic.next(nextStep);
  }

  /**
   * updates data stream aggregating received data
   * @param changes
   */
  updateData(changes: DataObject) {
    const data = this._data.getValue();
    this._data.next({ ...data, ...changes });
  }
}
